import { mapMutations } from 'vuex'
import API from '@/services/api'
import { firestore } from '@/services/firebase'
import { workOrderLooksFull } from '@/helpers/toLiteWorkOrder'

/**
 * Shared alert lifecycle logic (permissions, dialogs, API calls) used by any
 * view that needs to operate on production alerts: AlertsDashboard.vue (all
 * alerts, entered via the Alerts Dashboard route) and WOGeneralInfo.vue (alerts
 * scoped to a single work order, entered via ProcessWorkOrder.vue).
 *
 * Consumers are expected to provide:
 * - `this.companyId`, `this.currentUserId`
 * - `this.settingProcesses` (the "Processes" setting, used for coordinatedProcessIds)
 * - `this.setErrorItems` (mapMutations, included here as a fallback)
 * - alerts enriched with `workOrder` (lite or full) and `alertWOIsClosed`
 *   (Boolean) before being passed to any of these methods. Make Changes / Edit
 *   fetch the full work order on demand via `ensureFullWorkOrder`.
 * - a `reloadAlerts()` method (optional) to refresh the alerts list/listener
 *   after a mutating action succeeds
 * - optional `fetchFullWorkOrderForAlert(alert)` when the list only holds a
 *   lite work order (AlertsDashboard). WOGeneralInfo already has the full WO.
 */
export default {
    data: () => ({
        actionLoading: false,
        selectedAlert: null,
        timelineDialog: false,
        supervisorReviewDialog: false,
        supervisorAction: null,
        supervisorNotes: '',
        supervisorFormValid: false,
        makeChangesDialog: false,
        approveDialog: false,
        approveAction: null,
        approveNotes: '',
        approveFormValid: false,
        deleteAlertDialog: false,
        deleteNonExecutableDialog: false,
        canApproveAlerts: false,
        OCs: [],
        positions: {},
        makeChangesOpening: false,
        openingMakeChangesAlertId: null,
        rules: {
            required: v => !!v || 'This field is required',
        },
    }),
    computed: {
        coordinatedProcessIds() {
            if (!this.currentUserId || !this.settingProcesses) return []
            const processes = this.settingProcesses.processes || []
            return processes
                .filter(
                    p =>
                        Array.isArray(p.coordinators) &&
                        p.coordinators.includes(this.currentUserId)
                )
                .map(p => p.id)
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        async loadCanApproveAlerts() {
            try {
                const {
                    data: { user },
                } = await API.getMyInfo()
                this.canApproveAlerts = !!(
                    user.permissions &&
                    user.permissions.includes('approveProductionAlerts')
                )
            } catch (_) {
                this.canApproveAlerts = false
            }
        },
        getAlertTypeColor(type) {
            const colors = {
                add: 'green',
                remove: 'red',
                edit: 'orange',
            }
            return colors[type] || 'grey'
        },
        getStatusColor(status) {
            const colors = {
                pending: 'orange',
                resolved: 'green',
                applied: 'blue',
                pending_approval: 'purple',
            }
            return colors[status] || 'grey'
        },
        formatStep(step) {
            return step ? step.replace(/_/g, ' ') : '-'
        },
        canResolveAlert(alert) {
            if (!alert) return false
            return alert.currentStep === 'supervisor' && !alert.alertWOIsClosed
        },
        canMakeChanges(alert) {
            if (!alert) return false
            return alert.currentStep === 'pm_designer' && !alert.alertWOIsClosed
        },
        canApproveAlert(alert) {
            if (!alert) return false
            if (
                alert.currentStep !== 'supervisor_approval' ||
                alert.alertWOIsClosed === true
            )
                return false
            return (
                this.canApproveAlerts ||
                this.coordinatedProcessIds.includes(alert.processId)
            )
        },
        canEditAlert(alert) {
            if (!alert) return false
            return (
                alert.status === 'pending' &&
                alert.currentStep !== 'pm_designer' &&
                !alert.alertWOIsClosed
            )
        },
        canDeleteNonExecutableAlert(alert) {
            if (!alert) return false
            return alert.alertWOIsClosed === true
        },
        canDeleteAlert(alert) {
            if (!alert) return false
            return (
                !alert.alertWOIsClosed &&
                alert.status === 'pending' &&
                alert.currentStep !== 'pm_designer'
            )
        },
        hasActionOptions(alert) {
            return (
                this.canEditAlert(alert) ||
                this.canResolveAlert(alert) ||
                this.canMakeChanges(alert) ||
                this.canApproveAlert(alert) ||
                this.canDeleteNonExecutableAlert(alert) ||
                this.canDeleteAlert(alert)
            )
        },
        alertActionKey(alert) {
            if (!alert) return null
            return `${alert.workOrderId || ''}:${alert.id || ''}`
        },
        isOpeningMakeChanges(alert) {
            if (!this.makeChangesOpening || !alert) return false
            return this.openingMakeChangesAlertId === this.alertActionKey(alert)
        },
        viewAlert(alert) {
            this.selectedAlert = alert
            this.timelineDialog = true
        },
        async ensureFullWorkOrder(alert) {
            if (!alert || alert.alertWOIsClosed) return alert
            if (workOrderLooksFull(alert.workOrder)) return alert

            if (typeof this.fetchFullWorkOrderForAlert === 'function') {
                return this.fetchFullWorkOrderForAlert(alert)
            }

            const workOrderId = alert.workOrderId || alert.workOrder?.id
            if (!workOrderId || !this.companyId) return alert

            try {
                const workOrderDoc = await firestore
                    .collection('companies')
                    .doc(this.companyId)
                    .collection('workOrders')
                    .doc(workOrderId)
                    .get()
                if (!workOrderDoc.exists) {
                    this.$set(alert, 'alertWOIsClosed', true)
                    return alert
                }
                this.$set(alert, 'workOrder', {
                    id: workOrderDoc.id,
                    ...workOrderDoc.data(),
                })
                this.$set(alert, 'alertWOIsClosed', false)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
            return alert
        },
        async editAlert(alert) {
            await this.ensureFullWorkOrder(alert)
            if (alert.alertWOIsClosed) return
            this.selectedAlert = alert
            this.alertFormDialog = true
        },
        closeAlertForm() {
            this.alertFormDialog = false
            this.selectedAlert = null
        },
        openResolveDialog(alert) {
            this.selectedAlert = alert
            this.supervisorAction = null
            this.supervisorNotes = ''
            this.supervisorReviewDialog = true
        },
        async openMakeChangesDialog(alert) {
            this.makeChangesOpening = true
            this.openingMakeChangesAlertId = this.alertActionKey(alert)
            try {
                const projectId = alert.workOrder
                    ? alert.workOrder.projectId
                    : null
                const fullPromise = this.ensureFullWorkOrder(alert)
                const ocsPromise = projectId
                    ? API.getOCsAndPositions(projectId, this.companyId)
                    : fullPromise.then(updated => {
                          if (updated.alertWOIsClosed) {
                              return { OCs: [], positions: {} }
                          }
                          return API.getOCsAndPositions(
                              updated.workOrder && updated.workOrder.projectId,
                              this.companyId
                          )
                      })
                const [updated, { OCs, positions }] = await Promise.all([
                    fullPromise,
                    ocsPromise,
                ])
                if (updated.alertWOIsClosed) {
                    return
                }
                this.OCs = OCs || []
                this.positions = positions || {}
                this.selectedAlert = updated
                this.makeChangesDialog = true
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.makeChangesOpening = false
                this.openingMakeChangesAlertId = null
            }
        },
        openApproveDialog(alert) {
            this.selectedAlert = alert
            this.approveAction = null
            this.approveNotes = ''
            this.approveDialog = true
        },
        openDeleteAlertDialog(alert) {
            this.selectedAlert = alert
            this.deleteAlertDialog = true
        },
        openDeleteNonExecutableDialog(alert) {
            this.selectedAlert = alert
            this.deleteNonExecutableDialog = true
        },
        handleTimelineEdit(alert) {
            this.timelineDialog = false
            this.editAlert(alert)
        },
        handleTimelineResolve(alert) {
            this.timelineDialog = false
            this.openResolveDialog(alert)
        },
        handleTimelineMakeChanges(alert) {
            this.openMakeChangesDialog(alert).then(() => {
                if (this.makeChangesDialog) {
                    this.timelineDialog = false
                }
            })
        },
        handleTimelineApprove(alert) {
            this.timelineDialog = false
            this.openApproveDialog(alert)
        },
        async submitSupervisorReview() {
            if (!this.$refs.supervisorForm.validate()) {
                return
            }

            try {
                this.actionLoading = true

                // Ensure action is a valid string value
                const action = String(this.supervisorAction || '').trim()
                if (action !== 'resolve' && action !== 'forward') {
                    throw new Error('Invalid action value')
                }

                // Build params object with all required fields
                const params = {
                    alertId: this.selectedAlert.id,
                    action: action,
                    resolutionNotes: this.supervisorNotes || '',
                }

                // Include alert data (required for validation when forwarding)
                if (this.selectedAlert) {
                    if (this.selectedAlert.alertType) {
                        params.alertType = this.selectedAlert.alertType
                    }
                    if (this.selectedAlert.classification) {
                        params.classification = this.selectedAlert.classification
                    }
                    if (this.selectedAlert.alertMessage) {
                        params.alertMessage = this.selectedAlert.alertMessage
                    }
                    if (this.selectedAlert.selectedPartNumbers) {
                        params.selectedPartNumbers = this.selectedAlert.selectedPartNumbers
                    }
                    if (
                        this.selectedAlert.addAssemblyComponents !== undefined
                    ) {
                        params.addAssemblyComponents = this.selectedAlert.addAssemblyComponents
                    }
                    if (
                        this.selectedAlert.removeAssemblyComponents !==
                        undefined
                    ) {
                        params.removeAssemblyComponents = this.selectedAlert.removeAssemblyComponents
                    }
                }

                await API.supervisorReviewAlert(
                    this.selectedAlert.workOrderId,
                    params
                )
                this.supervisorReviewDialog = false
                if (typeof this.reloadAlerts === 'function') {
                    this.reloadAlerts()
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.actionLoading = false
            }
        },
        async submitApprove() {
            if (!this.$refs.approveForm.validate()) {
                return
            }

            try {
                this.actionLoading = true
                await API.supervisorApproveAlert(
                    this.selectedAlert.workOrderId,
                    {
                        alertId: this.selectedAlert.id,
                        action: this.approveAction,
                        notes: this.approveNotes,
                    }
                )
                this.approveDialog = false
                if (typeof this.reloadAlerts === 'function') {
                    this.reloadAlerts()
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.actionLoading = false
            }
        },
        async confirmDeleteAlert() {
            if (!this.selectedAlert) return
            try {
                this.actionLoading = true
                await API.deleteAlert(
                    this.selectedAlert.workOrderId,
                    this.selectedAlert.id
                )
                if (typeof this.reloadAlerts === 'function') {
                    this.reloadAlerts()
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.actionLoading = false
                this.deleteAlertDialog = false
                this.selectedAlert = null
            }
        },
        async confirmDeleteNonExecutableAlert() {
            if (!this.selectedAlert) return
            try {
                this.actionLoading = true
                await API.deleteAlert(
                    this.selectedAlert.workOrderId,
                    this.selectedAlert.id
                )
                this.deleteNonExecutableDialog = false
                this.selectedAlert = null
                if (typeof this.reloadAlerts === 'function') {
                    this.reloadAlerts()
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.actionLoading = false
            }
        },
    },
}
